// Multi-language support for Lithuanian and English

export type Language = 'lt' | 'en';

export interface Translations {
  // Navigation
  login: string;
  getStarted: string;
  tryDemo: string;
  bookDemo: string;

  // Hero Section
  heroTag: string;
  heroTitle: string;
  heroTitleHighlight: string;
  heroSubtitle: string;
  startFreeTrial: string;
  learnMore: string;
  watchVideo: string;

  // Contact Form
  contactFormTitle: string;
  contactFormSubtitle: string;
  formName: string;
  formNamePlaceholder: string;
  formEmail: string;
  formEmailPlaceholder: string;
  formCompany: string;
  formCompanyPlaceholder: string;
  formEmployees: string;
  formEmployeesPlaceholder: string;
  formSubmit: string;
  formSuccess: string;
  formDisclaimer: string;
  employeeOptions1: string;
  employeeOptions2: string;
  employeeOptions3: string;
  employeeOptions4: string;
  employeeOptions5: string;

  // Demo Section
  demoTitle: string;
  demoSubtitle: string;
  demoPlaceholder: string;
  demoButton: string;
  demoDisclaimer: string;

  // Statistics
  statROI: string;
  statROIDesc: string;
  statTimeSaved: string;
  statTimeSavedDesc: string;
  statCostSaved: string;
  statCostSavedDesc: string;
  statProcesses: string;
  statProcessesDesc: string;

  // Social Proof
  trustedBy: string;

  // Benefits
  whyChooseTitle: string;
  whyChooseSubtitle: string;
  fastImplementation: string;
  fastImplementationDesc: string;
  costEffective: string;
  costEffectiveDesc: string;
  secureCompliant: string;
  secureCompliantDesc: string;
  noCodeRequired: string;
  noCodeRequiredDesc: string;

  // Features
  featuresTitle: string;
  featuresSubtitle: string;
  dataInputTitle: string;
  dataInputDesc: string;
  documentGenTitle: string;
  documentGenDesc: string;
  paymentProcessingTitle: string;
  paymentProcessingDesc: string;
  eSignatureTitle: string;
  eSignatureDesc: string;
  notificationsTitle: string;
  notificationsDesc: string;
  identityVerificationTitle: string;
  identityVerificationDesc: string;

  // How It Works
  howItWorksTitle: string;
  howItWorksSubtitle: string;
  step1Title: string;
  step1Desc: string;
  step2Title: string;
  step2Desc: string;
  step3Title: string;
  step3Desc: string;
  step4Title: string;
  step4Desc: string;

  // Demo Video
  demoVideoTitle: string;
  demoVideoSubtitle: string;
  watchDemo: string;

  // Testimonials
  testimonialsTitle: string;
  testimonialsSubtitle: string;
  testimonial1Quote: string;
  testimonial1Author: string;
  testimonial1Role: string;
  testimonial2Quote: string;
  testimonial2Author: string;
  testimonial2Role: string;
  testimonial3Quote: string;
  testimonial3Author: string;
  testimonial3Role: string;

  // Founder's Message
  founderTitle: string;
  founderQuote: string;
  founderName: string;
  founderRole: string;

  // Pricing
  pricingTitle: string;
  pricingSubtitle: string;
  pricingMonthly: string;
  pricingYearly: string;
  pricingSave: string;
  starterPlan: string;
  starterPrice: string;
  starterDesc: string;
  starterFeature1: string;
  starterFeature2: string;
  starterFeature3: string;
  starterFeature4: string;
  proPlan: string;
  proPrice: string;
  proDesc: string;
  proFeature1: string;
  proFeature2: string;
  proFeature3: string;
  proFeature4: string;
  proFeature5: string;
  enterprisePlan: string;
  enterprisePrice: string;
  enterpriseDesc: string;
  enterpriseFeature1: string;
  enterpriseFeature2: string;
  enterpriseFeature3: string;
  enterpriseFeature4: string;
  enterpriseFeature5: string;
  mostPopular: string;
  choosePlan: string;
  contactSales: string;
  perMonth: string;

  // CTA
  ctaTitle: string;
  ctaSubtitle: string;
  startYourFreeTrial: string;
  noCreditCard: string;

  // EU Funding
  euFundingTitle: string;
  euFundingProjectName: string;
  euFundingDescription: string;
  euFundingPeriod: string;
  euFundingPeriodValue: string;
  euFundingTotal: string;
  euFundingTotalValue: string;
  euFundingEU: string;
  euFundingEUValue: string;
  euFundingDisclaimer: string;

  // Footer
  footerTagline: string;
  product: string;
  features: string;
  pricing: string;
  documentation: string;
  integrations: string;
  company: string;
  aboutUs: string;
  contact: string;
  contactUs: string;
  careers: string;
  blog: string;
  legal: string;
  privacyPolicy: string;
  termsOfService: string;
  gdprCompliance: string;
  allRightsReserved: string;
  madeWithLove: string;
}

export const translations: Record<Language, Translations> = {
  lt: {
    // Navigation
    login: 'Prisijungti',
    getStarted: 'Susisiekti',
    tryDemo: 'Gauti demonstraciją',
    bookDemo: 'Užsisakyti demonstraciją',

    // Hero Section
    heroTag: 'Procesų automatizavimo platforma',
    heroTitle: 'Automatizuokite verslo procesus',
    heroTitleHighlight: 'be kodo',
    heroSubtitle: 'Sukurkite galingas darbo eigas per kelias minutes. Automatizuokite duomenų rinkimą, mokėjimus, dokumentų pasirašymą ir daugiau su mūsų platforma be kodo.',
    startFreeTrial: 'Susisiekti su mumis',
    learnMore: 'Sužinoti daugiau',
    watchVideo: 'Žiūrėti vaizdo įrašą',

    // Contact Form
    contactFormTitle: 'Susisiekite su mumis',
    contactFormSubtitle: 'Užpildykite formą ir mes su jumis susisieksime per 24 valandas.',
    formName: 'Vardas',
    formNamePlaceholder: 'Įveskite savo vardą',
    formEmail: 'El. paštas',
    formEmailPlaceholder: 'Įveskite el. paštą',
    formCompany: 'Įmonė',
    formCompanyPlaceholder: 'Įveskite įmonės pavadinimą',
    formEmployees: 'Darbuotojų skaičius',
    formEmployeesPlaceholder: 'Įveskite darbuotojų skaičių',
    formSubmit: 'Siųsti užklausą',
    formSuccess: 'Ačiū! Jūsų užklausa išsiųsta. Susisieksime su jumis artimiausiu metu.',
    formDisclaimer: 'Su jumis susisieksime per 24 valandas.',
    employeeOptions1: '1–10',
    employeeOptions2: '11–50',
    employeeOptions3: '51–200',
    employeeOptions4: '201–500',
    employeeOptions5: '500+',

    // Demo Section
    demoTitle: 'Gaukite nemokamą konsultaciją',
    demoSubtitle: 'Užpildykite formą ir mūsų specialistai su jumis susisieks.',
    demoPlaceholder: 'Įveskite el. paštą',
    demoButton: 'Gauti konsultaciją',
    demoDisclaimer: 'Nemokama konsultacija. Susisieksime per 24 val.',

    // Statistics
    statROI: '2-5x',
    statROIDesc: 'Vidutinė investicijų grąža',
    statTimeSaved: '10-15 min',
    statTimeSavedDesc: 'Sutaupyta per darbo eigos užduotį',
    statCostSaved: '0,05 Eur',
    statCostSavedDesc: 'Už proceso įvykdymą',
    statProcesses: '',
    statProcessesDesc: '',

    // Social Proof
    trustedBy: 'Pasitiki pirmaujantys Baltijos verslai',

    // Benefits
    whyChooseTitle: 'Kodėl rinktis recodinAI?',
    whyChooseSubtitle: 'Mūsų platforma sukurta taip, kad procesų automatizavimas būtų paprastas ir prieinamas kiekvienam verslui.',
    fastImplementation: 'Greitas įdiegimas',
    fastImplementationDesc: 'Sukurkite ir paleiskite darbo eigas per minutes, o ne mėnesius. Programavimo žinių nereikia.',
    costEffective: 'Efektyvus ir naudingas',
    costEffectiveDesc: 'Mokėkite tik 0,05 Eur už proceso įvykdymą. Jokių paslėptų mokesčių.',
    secureCompliant: 'Saugus ir atitinkantis reikalavimus',
    secureCompliantDesc: 'Įmontuotas saugumas ir atitiktis Baltijos šalių bei ES standartams, įskaitant BDAR.',
    noCodeRequired: 'Be kodo',
    noCodeRequiredDesc: 'Intuityvus no-code įrankis leidžia bet kam kurti sudėtingas automatizacijas.',

    // Features
    featuresTitle: 'Viskas, ko reikia procesų automatizavimui',
    featuresSubtitle: 'Galingi darbo eigos komponentai, paruošti naudojimui',
    dataInputTitle: 'Duomenų įvedimas ir formos',
    dataInputDesc: 'Kurkite dinamines formas su validacijomis. Integruokitės su Google Drive, AWS S3 ir daugiau.',
    documentGenTitle: 'Dokumentų generavimas',
    documentGenDesc: 'Automatiškai generuokite dokumentus iš šablonų su dinaminiu duomenų įterpimu.',
    paymentProcessingTitle: 'Mokėjimų apdorojimas',
    paymentProcessingDesc: 'Integruoti mokėjimai su Stripe. 2% už transakciją.',
    eSignatureTitle: 'Elektroninis parašas',
    eSignatureDesc: 'Integracija su MarkID elektroninio parašo sistema.',
    notificationsTitle: 'Pranešimai',
    notificationsDesc: 'Siųskite automatinius SMS ir el. pašto pranešimus su dinaminiais šablonais.',
    identityVerificationTitle: 'Tapatybės patvirtinimas',
    identityVerificationDesc: 'Saugus tapatybės patvirtinimas su Mobile-ID ir Smart-ID palaikymu.',

    // How It Works
    howItWorksTitle: 'Kaip tai veikia',
    howItWorksSubtitle: 'Keturi paprasti žingsniai iki visiškai automatizuotų procesų',
    step1Title: 'Registracija',
    step1Desc: 'Sukurkite paskyrą per kelias sekundes su Google ar el. paštu.',
    step2Title: 'Sukurkite darbo eigą',
    step2Desc: 'Vilkite ir meskite darbo eigos žingsnius iš mūsų bibliotekos.',
    step3Title: 'Konfigūruokite',
    step3Desc: 'Nustatykite formas, šablonus ir automatizacijos taisykles.',
    step4Title: 'Paleiskite',
    step4Desc: 'Aktyvuokite ir leiskite platformai dirbti už jus.',

    // Demo Video
    demoVideoTitle: 'Pamatykite recodinAI veikime',
    demoVideoSubtitle: 'Sužinokite, kaip mūsų platforma gali transformuoti jūsų verslo procesus vos per kelias minutes.',
    watchDemo: 'Žiūrėti demonstraciją',

    // Testimonials
    testimonialsTitle: 'Mūsų klientų atsiliepimai',
    testimonialsSubtitle: 'Sužinokite, ką sako verslai, naudojantys recodinAI',
    testimonial1Quote: 'recodinAI pakeitė tai, kaip tvarkome klientų sutartis. Kas anksčiau užtrukdavo valandas, dabar įvyksta automatiškai per minutes.',
    testimonial1Author: 'Jonas Jonaitis',
    testimonial1Role: 'Direktorius, UAB „Technologijos"',
    testimonial2Quote: 'Integracija su Lietuvos mokėjimo sistemomis buvo sklandi. Sutaupėme 70 % laiko apdorojant operacijas.',
    testimonial2Author: 'Milda Petraitytė',
    testimonial2Role: 'Finansų vadovė, MB „Inovacijos"',
    testimonial3Quote: 'Pagaliau radome sprendimą, kuris supranta Baltijos rinkos poreikius. E. parašų integracija yra nepriekaištinga.',
    testimonial3Author: 'Tomas Tomauskas',
    testimonial3Role: 'IT vadovas, UAB „Pasaulis"',

    // Founder's Message
    founderTitle: 'Įrankio kūrėjo žodis',
    founderQuote: 'Šį įrankį sukūrėme tam, kad jūs galėtumėte plėsti savo veiklą, auginti verslus ir pasiekti daugiau su mažesnėmis pastangomis. Mūsų tikslas – padėti jums automatizuoti kasdienius procesus ir sutelkti dėmesį į tai, kas tikrai svarbu – jūsų verslo augimą.',
    founderName: 'Rokas Ulickas',
    founderRole: 'Įkūrėjas ir vadovas, recodinAI',

    // Pricing
    pricingTitle: 'Paprasta, skaidri kainodara',
    pricingSubtitle: 'Pasirinkite planą, kuris tinka jūsų verslui. Be paslėptų mokesčių.',
    pricingMonthly: 'Mėnesinis',
    pricingYearly: 'Metinis',
    pricingSave: 'Sutaupykite 20 %',
    starterPlan: 'Pradedantysis',
    starterPrice: '23',
    starterDesc: 'Puiki pradžia mažoms komandoms',
    starterFeature1: 'Iki 100 vykdymų per mėnesį',
    starterFeature2: '3 darbo eigos modeliai',
    starterFeature3: 'El. pašto pranešimai',
    starterFeature4: 'Bazinė pagalba',
    proPlan: 'Profesionalus',
    proPrice: '79',
    proDesc: 'Augančioms komandoms ir verslams',
    proFeature1: 'Iki 1 000 vykdymų per mėnesį',
    proFeature2: 'Neriboti darbo eigos modeliai',
    proFeature3: 'SMS ir el. pašto pranešimai',
    proFeature4: 'E. parašo integracija',
    proFeature5: 'Prioritetinė pagalba',
    enterprisePlan: 'Įmonė',
    enterprisePrice: 'Susisiekite',
    enterpriseDesc: 'Didelėms organizacijoms su specialiais poreikiais',
    enterpriseFeature1: 'Neriboti vykdymai',
    enterpriseFeature2: 'Individualus pritaikymas',
    enterpriseFeature3: 'Dedikuota pagalba',
    enterpriseFeature4: 'API prieiga',
    enterpriseFeature5: 'SLA garantijos',
    mostPopular: 'Populiariausias',
    choosePlan: 'Pasirinkti',
    contactSales: 'Susisiekti',
    perMonth: '/mėn.',

    // CTA
    ctaTitle: 'Pasiruošę automatizuoti savo verslą?',
    ctaSubtitle: 'Prisijunkite prie šimtų verslų, kurie jau naudoja recodinAI darbo eigų automatizavimui.',
    startYourFreeTrial: 'Susisiekti su mumis',
    noCreditCard: 'Nemokama konsultacija',

    // EU Funding
    euFundingTitle: 'ES projekto finansavimas',
    euFundingProjectName: 'Darbų eigos automatizavimo platformos įgyvendinimas panaudojant dirbtinio intelekto sprendimus',
    euFundingDescription: 'MB "Kodas nuo nulio" įgyvendina projektą Nr. 02-018-K-0389, kurio tikslas yra sukurti SaaS (Software as a Service) pagrindu veikiančią platformą, skirtą optimizuoti verslo procesus mažoms ir vidutinėms įmonėms. Ši platforma leis įmonėms be programavimo žinių lengvai kurti ir automatizuoti jų verslo procesus, taip sumažinant klaidų riziką ir operacines sąnaudas.',
    euFundingPeriod: 'Projekto įgyvendinimo laikotarpis',
    euFundingPeriodValue: '06/2024 - 09/2025',
    euFundingTotal: 'Bendra projekto vertė',
    euFundingTotalValue: '101 000 EUR',
    euFundingEU: 'Finansuojama ES lėšomis',
    euFundingEUValue: '85 980 EUR',
    euFundingDisclaimer: 'Finansuoja Europos Sąjunga',

    // Footer
    footerTagline: 'Procesų automatizavimo platforma be kodo Baltijos verslams.',
    product: 'Produktas',
    features: 'Funkcijos',
    pricing: 'Kainodara',
    documentation: 'Dokumentacija',
    integrations: 'Integracijos',
    company: 'Įmonė',
    aboutUs: 'Apie mus',
    contact: 'Kontaktai',
    contactUs: 'Parašykite mums',
    careers: 'Karjera',
    blog: 'Tinklaraštis',
    legal: 'Teisinė informacija',
    privacyPolicy: 'Privatumo politika',
    termsOfService: 'Paslaugų sąlygos',
    gdprCompliance: 'BDAR atitiktis',
    allRightsReserved: 'Visos teisės saugomos.',
    madeWithLove: 'Sukurta su meile Lietuvoje',
  },
  en: {
    // Navigation
    login: 'Log in',
    getStarted: 'Contact us',
    tryDemo: 'Get a demo',
    bookDemo: 'Book a demo',

    // Hero Section
    heroTag: 'Process Automation Platform',
    heroTitle: 'Automate your business processes',
    heroTitleHighlight: 'without code',
    heroSubtitle: 'Create powerful workflows in minutes. Automate data collection, payments, document signing, and more with our no-code platform.',
    startFreeTrial: 'Contact us',
    learnMore: 'Learn more',
    watchVideo: 'Watch video',

    // Contact Form
    contactFormTitle: 'Contact us',
    contactFormSubtitle: 'Fill out the form and we will contact you within 24 hours.',
    formName: 'Name',
    formNamePlaceholder: 'Enter your name',
    formEmail: 'Email',
    formEmailPlaceholder: 'Enter your email',
    formCompany: 'Company',
    formCompanyPlaceholder: 'Enter company name',
    formEmployees: 'Number of employees',
    formEmployeesPlaceholder: 'Enter number of employees',
    formSubmit: 'Send request',
    formSuccess: 'Thank you! Your request has been sent. We will contact you soon.',
    formDisclaimer: 'We will contact you within 24 hours.',
    employeeOptions1: '1–10',
    employeeOptions2: '11–50',
    employeeOptions3: '51–200',
    employeeOptions4: '201–500',
    employeeOptions5: '500+',

    // Demo Section
    demoTitle: 'Get a free consultation',
    demoSubtitle: 'Fill out the form and our specialists will contact you.',
    demoPlaceholder: 'Enter your email',
    demoButton: 'Get consultation',
    demoDisclaimer: 'Free consultation. We will contact you within 24 hours.',

    // Statistics
    statROI: '2-5x',
    statROIDesc: 'Average return on investment',
    statTimeSaved: '10-15 min',
    statTimeSavedDesc: 'Saved per workflow task',
    statCostSaved: '0.05 EUR',
    statCostSavedDesc: 'Per process execution',
    statProcesses: '',
    statProcessesDesc: '',

    // Social Proof
    trustedBy: 'Trusted by leading Baltic businesses',

    // Benefits
    whyChooseTitle: 'Why choose recodinAI?',
    whyChooseSubtitle: 'Our platform is designed to make process automation simple and accessible for every business.',
    fastImplementation: 'Fast implementation',
    fastImplementationDesc: 'Create and deploy workflows in minutes, not months. No programming knowledge required.',
    costEffective: 'Effective & beneficial',
    costEffectiveDesc: 'Pay only EUR 0.05 per process execution. No hidden fees.',
    secureCompliant: 'Secure & compliant',
    secureCompliantDesc: 'Built-in security and compliance for Baltic countries and EU standards, including GDPR.',
    noCodeRequired: 'No code required',
    noCodeRequiredDesc: 'Intuitive no-code tool allows anyone to create complex automations.',

    // Features
    featuresTitle: 'Everything you need for process automation',
    featuresSubtitle: 'Powerful workflow components, ready to use',
    dataInputTitle: 'Data input & forms',
    dataInputDesc: 'Create dynamic forms with validations. Integrate with Google Drive, AWS S3, and more.',
    documentGenTitle: 'Document generation',
    documentGenDesc: 'Automatically generate documents from templates with dynamic data insertion.',
    paymentProcessingTitle: 'Payment processing',
    paymentProcessingDesc: 'Integrated payments with Stripe. 2% per transaction.',
    eSignatureTitle: 'E-Signature',
    eSignatureDesc: 'Integration with MarkID electronic signature system.',
    notificationsTitle: 'Notifications',
    notificationsDesc: 'Send automated SMS and email notifications with dynamic templates.',
    identityVerificationTitle: 'Identity verification',
    identityVerificationDesc: 'Secure identity verification with Mobile-ID and Smart-ID support.',

    // How It Works
    howItWorksTitle: 'How it works',
    howItWorksSubtitle: 'Four simple steps to fully automated processes',
    step1Title: 'Sign up',
    step1Desc: 'Create your account in seconds with Google or email.',
    step2Title: 'Build workflow',
    step2Desc: 'Drag and drop workflow steps from our library.',
    step3Title: 'Configure',
    step3Desc: 'Set up forms, templates, and automation rules.',
    step4Title: 'Launch',
    step4Desc: 'Activate and let the platform work for you.',

    // Demo Video
    demoVideoTitle: 'See recodinAI in action',
    demoVideoSubtitle: 'Learn how our platform can transform your business processes in just minutes.',
    watchDemo: 'Watch demo',

    // Testimonials
    testimonialsTitle: 'What our customers say',
    testimonialsSubtitle: 'See what businesses using recodinAI have to say',
    testimonial1Quote: 'recodinAI transformed how we handle customer contracts. What used to take hours now happens automatically in minutes.',
    testimonial1Author: 'Jonas Jonaitis',
    testimonial1Role: 'Director, UAB Technologijos',
    testimonial2Quote: 'Integration with Lithuanian payment systems was seamless. We saved 70% of the time spent processing transactions.',
    testimonial2Author: 'Milda Petraityte',
    testimonial2Role: 'Finance Manager, MB Inovacijos',
    testimonial3Quote: 'We finally found a solution that understands the Baltic market needs. The e-signature integration is flawless.',
    testimonial3Author: 'Tomas Tomauskas',
    testimonial3Role: 'IT Manager, UAB Pasaulis',

    // Founder's Message
    founderTitle: 'A word from the founder',
    founderQuote: 'We created this tool so that you can expand your operations, grow your business, and achieve more with less effort. Our goal is to help you automate everyday processes and focus on what truly matters – growing your business.',
    founderName: 'Rokas Ulickas',
    founderRole: 'Founder & CEO, recodinAI',

    // Pricing
    pricingTitle: 'Simple, transparent pricing',
    pricingSubtitle: 'Choose the plan that fits your business. No hidden fees.',
    pricingMonthly: 'Monthly',
    pricingYearly: 'Yearly',
    pricingSave: 'Save 20%',
    starterPlan: 'Starter',
    starterPrice: '23',
    starterDesc: 'Perfect for small teams getting started',
    starterFeature1: 'Up to 100 executions/month',
    starterFeature2: '3 workflow models',
    starterFeature3: 'Email notifications',
    starterFeature4: 'Basic support',
    proPlan: 'Professional',
    proPrice: '79',
    proDesc: 'For growing teams and businesses',
    proFeature1: 'Up to 1,000 executions/month',
    proFeature2: 'Unlimited workflow models',
    proFeature3: 'SMS + email notifications',
    proFeature4: 'E-signature integration',
    proFeature5: 'Priority support',
    enterprisePlan: 'Enterprise',
    enterprisePrice: 'Contact us',
    enterpriseDesc: 'For large organizations with custom needs',
    enterpriseFeature1: 'Unlimited executions',
    enterpriseFeature2: 'Custom development',
    enterpriseFeature3: 'Dedicated support',
    enterpriseFeature4: 'API access',
    enterpriseFeature5: 'SLA guarantees',
    mostPopular: 'Most popular',
    choosePlan: 'Choose plan',
    contactSales: 'Contact sales',
    perMonth: '/mo',

    // CTA
    ctaTitle: 'Ready to automate your business?',
    ctaSubtitle: 'Join hundreds of businesses already using recodinAI to automate their workflows.',
    startYourFreeTrial: 'Contact us',
    noCreditCard: 'Free consultation',

    // EU Funding
    euFundingTitle: 'EU Project Funding',
    euFundingProjectName: 'Implementation of workflow automation platform using artificial intelligence solutions',
    euFundingDescription: 'MB "Kodas nuo nulio" is implementing project No. 02-018-K-0389, which aims to create a SaaS (Software as a Service) based platform designed to optimize business processes for small and medium-sized enterprises. This platform will enable companies without programming knowledge to easily create and automate their business processes, reducing the risk of errors and operational costs.',
    euFundingPeriod: 'Project implementation period',
    euFundingPeriodValue: '06/2024 - 09/2025',
    euFundingTotal: 'Total project value',
    euFundingTotalValue: '101,000 EUR',
    euFundingEU: 'EU funding',
    euFundingEUValue: '85,980 EUR',
    euFundingDisclaimer: 'Funded by the European Union',

    // Footer
    footerTagline: 'No-code process automation platform for Baltic businesses.',
    product: 'Product',
    features: 'Features',
    pricing: 'Pricing',
    documentation: 'Documentation',
    integrations: 'Integrations',
    company: 'Company',
    aboutUs: 'About us',
    contact: 'Contact',
    contactUs: 'Write to us',
    careers: 'Careers',
    blog: 'Blog',
    legal: 'Legal',
    privacyPolicy: 'Privacy policy',
    termsOfService: 'Terms of service',
    gdprCompliance: 'GDPR compliance',
    allRightsReserved: 'All rights reserved.',
    madeWithLove: 'Made with love in Lithuania',
  },
};

export function getTranslations(language: Language): Translations {
  return translations[language];
}
